import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from 'tss-react/mui';
import {FormattedMessage, useIntl} from 'react-intl';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import SignProgressStepper from '../components/SignProgressStepper';
import SignatureDialog from '../components/SignatureDialog';
import SignatureField from '../components/SignatureField';
import {submitSignature} from '../api/signingApi';

const useStyles = makeStyles()((theme) => ({
  container: {
    maxWidth: 600,
    margin: '0 auto',
    padding: '24px 16px 48px',
    '@media (min-width: 768px)': {
      padding: '40px 24px 64px',
    },
    // Wide enough for the template's own 800px layout, so on a desktop it reflows nothing and
    // reads exactly like the pdf.
    '@media (min-width: 1024px)': {
      maxWidth: 880,
    },
  },
  document: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    overflow: 'hidden',
  },
  documentFrame: {
    display: 'block',
    width: '100%',
    border: 'none',
  },
  readingBar: {
    position: 'sticky',
    bottom: 0,
    zIndex: 5,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 12px',
    marginTop: 8,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    boxShadow: `0 -2px 8px ${theme.palette.divider}`,
  },
  progressTrack: {
    flex: 1,
    height: 6,
    borderRadius: 3,
    backgroundColor: theme.palette.divider,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: theme.palette.primary.main,
    transition: 'width 0.15s linear',
  },
  progressLabel: {
    fontSize: theme.typography.pxToRem(13),
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  gateHint: {
    fontSize: theme.typography.pxToRem(13),
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.secondary,
    marginTop: 8,
  },
  signatureTitle: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 700,
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.primary,
    margin: '32px 0 8px',
  },
  consentRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 4,
    margin: '16px 0',
  },
  consentText: {
    fontSize: theme.typography.pxToRem(15),
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.primary,
    lineHeight: 1.6,
    paddingTop: 10,
  },
  submitButton: {
    width: '100%',
    padding: '14px 24px',
    borderRadius: 30,
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 'bold',
    fontFamily: theme.typography.fontFamily,
  },
  errorMessage: {
    color: theme.palette.error.main,
    fontSize: theme.typography.pxToRem(14),
    fontFamily: theme.typography.fontFamily,
    marginTop: 12,
    textAlign: 'center',
  },
}));

// The document template is a fixed 800px print layout. This overlay reflows it for reading on a phone.
// It is appended to the copy shown in the iframe only — the stored HTML the PDF is rendered from is untouched,
// so the content is identical and only the line wrapping differs.
const VIEWER_STYLE = `
<style>
  html, body { margin: 0 !important; padding: 0 !important; }
  body { overflow-x: hidden !important; -webkit-text-size-adjust: 100% !important; }
  .container { width: auto !important; max-width: 800px !important; margin: 0 auto !important; }
  .pageContent { padding: 12px !important; box-sizing: border-box !important; }
  div[style*="display: flex"] { flex-wrap: wrap !important; }
  img { max-width: 100% !important; height: auto !important; }
  table { width: 100% !important; max-width: 100% !important; table-layout: fixed !important; }
  td { height: auto !important; padding: 10px 8px !important; overflow-wrap: break-word !important; }
  a { overflow-wrap: break-word !important; }
  .bullet-text { width: auto !important; flex: 1 1 auto !important; }
  .page-break { display: none !important; }

  /* Evaluated against the iframe's own width. Once the 800px layout is squeezed this far the
     lines are short, so the crowding comes from leading and cell padding rather than font size. */
  @media (max-width: 700px) {
    body { line-height: 1.65 !important; }
    td { padding: 12px 10px !important; }
    p, li { margin-top: 0.6em !important; margin-bottom: 0.6em !important; }
    h1, h2, h3 { margin-top: 0.8em !important; margin-bottom: 0.4em !important; line-height: 1.3 !important; }
  }
</style>`;

const SignScreen = ({signKey, model, onSigned, onSubmit = submitSignature, showStepper = true}) => {
  const {classes} = useStyles();
  const intl = useIntl();
  const frameRef = useRef(null);
  const documentRef = useRef(null);
  const [signatureImage, setSignatureImage] = useState('');
  const [padOpen, setPadOpen] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [frameHeight, setFrameHeight] = useState(400);
  const [readProgress, setReadProgress] = useState(0);
  const [hasReadToEnd, setHasReadToEnd] = useState(false);
  const [documentReady, setDocumentReady] = useState(false);

  const viewerHtml = useMemo(() => {
    const html = model.documentHtml || '';

    return /<\/body>/i.test(html) ? html.replace(/<\/body>/i, () => `${VIEWER_STYLE}</body>`) : html + VIEWER_STYLE;
  }, [model.documentHtml]);

  const measureFrame = useCallback(() => {
    const doc = frameRef.current?.contentDocument;
    if (!doc?.body) return;

    setFrameHeight(Math.max(doc.documentElement.scrollHeight, doc.body.scrollHeight));
  }, []);

  /** Left alone, a link navigates the frame itself: sites that refuse to be framed show
   * "refused to connect", and our own pages load without scripts and show the noscript notice. */
  const retargetLinks = useCallback(() => {
    const doc = frameRef.current?.contentDocument;

    doc?.querySelectorAll('a[href]').forEach((anchor) => {
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
    });
  }, []);

  const handleFrameLoad = () => {
    measureFrame();
    setDocumentReady(true);
    retargetLinks();
    // The template pulls remote fonts; their metrics change the height after load fires.
    frameRef.current?.contentDocument?.fonts?.ready.then(measureFrame).catch(() => {});
  };

  useEffect(() => {
    window.addEventListener('resize', measureFrame);
    window.addEventListener('orientationchange', measureFrame);

    return () => {
      window.removeEventListener('resize', measureFrame);
      window.removeEventListener('orientationchange', measureFrame);
    };
  }, [measureFrame]);

  // The iframe grows to its full content height, so the page scroll is what reveals the document.
  // Measuring before it loads would read the 400px placeholder and latch as fully read straight away.
  const updateReadProgress = useCallback(() => {
    const node = documentRef.current;
    if (!node || !documentReady) return;

    const {height, top} = node.getBoundingClientRect();
    if (height <= 0) return;

    const revealed = Math.min(height, Math.max(0, window.innerHeight - top));
    const ratio = Math.min(1, revealed / height);

    setReadProgress(ratio);
    // Latched: scrolling back up must not re-lock a document that was already shown in full.
    if (ratio >= 0.995) setHasReadToEnd(true);
  }, [documentReady]);

  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        updateReadProgress();
      });
    };

    window.addEventListener('scroll', onScroll, {passive: true});
    window.addEventListener('resize', onScroll);
    updateReadProgress();

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [updateReadProgress, frameHeight]);

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      const result = await onSubmit(signKey, signatureImage);
      onSigned(result);
    } catch (err) {
      setError(err?.message || intl.formatMessage({id: 'sign.submitError'}));
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = Boolean(signatureImage) && consentAccepted && !loading;

  return (
    <div>
      {showStepper && <SignProgressStepper activeStep={1} />}
      <div className={classes.container}>
        <div className={classes.document} ref={documentRef}>
          {/* sandbox without allow-scripts blocks execution while keeping the template's own CSS intact.
              allow-popups lets a link open a tab; escape-sandbox keeps that tab out of this sandbox,
              so the opened site runs its own scripts normally. */}
          <iframe
            ref={frameRef}
            title={model.documentTitle || 'document'}
            className={classes.documentFrame}
            style={{height: frameHeight}}
            srcDoc={viewerHtml}
            sandbox='allow-same-origin allow-popups allow-popups-to-escape-sandbox'
            onLoad={handleFrameLoad}
          />
        </div>

        {!hasReadToEnd && (
          <div className={classes.readingBar} dir='rtl'>
            <span className={classes.progressLabel}>
              {intl.formatMessage({id: 'sign.reading.progress'}, {percent: Math.round(readProgress * 100)})}
            </span>
            <div className={classes.progressTrack}>
              <div className={classes.progressFill} style={{width: `${Math.round(readProgress * 100)}%`}} />
            </div>
          </div>
        )}

        <div className={classes.signatureTitle}>
          <FormattedMessage id='sign.pad.title' />
        </div>
        <SignatureField
          value={signatureImage}
          disabled={!hasReadToEnd}
          onOpen={() => setPadOpen(true)}
        />
        {!hasReadToEnd && (
          <div className={classes.gateHint}>
            <FormattedMessage id='sign.reading.gate' />
          </div>
        )}
        <SignatureDialog open={padOpen} onClose={() => setPadOpen(false)} onConfirm={setSignatureImage} />

        {/* dir on the element, not CSS: stylis-plugin-rtl rewrites CSS, so a `direction` rule here would flip back */}
        <div className={classes.consentRow} dir='rtl'>
          <Checkbox
            checked={consentAccepted}
            onChange={(event) => setConsentAccepted(event.target.checked)}
            inputProps={{'aria-label': intl.formatMessage({id: 'sign.consent.aria'})}}
          />
          <div className={classes.consentText}>{model.consentText}</div>
        </div>

        <Button
          variant='contained'
          color='primary'
          className={classes.submitButton}
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          {loading ? <CircularProgress size={24} color='inherit' /> : <FormattedMessage id='sign.submitButton' />}
        </Button>

        {error && <div className={classes.errorMessage}>{error}</div>}
      </div>
    </div>
  );
};

SignScreen.propTypes = {
  model: PropTypes.shape({
    consentText: PropTypes.string,
    documentHtml: PropTypes.string,
    documentTitle: PropTypes.string,
  }).isRequired,
  onSigned: PropTypes.func.isRequired,
  signKey: PropTypes.string.isRequired,
  onSubmit: PropTypes.func,
  showStepper: PropTypes.bool,
};

export default SignScreen;
